import React from "react";
import { motion } from "framer-motion";
import { Map, Users, ShieldCheck, ScrollText, TrendingUp } from "lucide-react";

/**
 * Big narrative pitch on the Landing page. Sits between the welcome hero
 * (branches + CTAs) and the "How It Works" step-by-step section. Reads
 * top-to-bottom as: problem → solution → mechanic → transformation → outcome.
 */
export default function AboutAtlas() {
    return (
        <section className="relative w-full px-6 py-20 sm:py-28 z-10" data-testid="about-atlas">
            <div className="max-w-5xl mx-auto">
                {/* Problem — high contrast, poster-style */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-4xl mx-auto"
                    data-testid="about-problem"
                >
                    <h2
                        className="font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tighter text-white leading-[0.98]"
                        style={{ textShadow: "0 3px 30px rgba(10,37,64,0.4)" }}
                    >
                        Traditional resumes are <span className="line-through decoration-arts decoration-[6px]">broken</span>.
                        <br className="hidden sm:block" />
                        <span className="text-arts">Modern career paths</span> are directionless.
                    </h2>
                </motion.div>

                {/* Solution — glass card, larger body text */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.05 }}
                    className="crystal rounded-3xl p-8 sm:p-12 mt-12 max-w-3xl mx-auto"
                    data-testid="about-solution"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-2xl bg-humanities flex items-center justify-center">
                            <Map className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-xs uppercase tracking-widest font-bold text-humanities">
                            What Project Atlas is
                        </p>
                    </div>
                    <p className="text-slate-800 text-lg sm:text-xl leading-[1.55] font-sans">
                        Project Atlas is an interactive playground for{" "}
                        <b className="text-slate-900">human collaboration</b>. It maps every known
                        discipline on Earth — from quantum physics to digital illustration — into a{" "}
                        <b className="text-humanities">single, explorable feed</b>.
                    </p>
                </motion.div>

                {/* Three-column mechanic breakdown */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="about-mechanics">
                    {[
                        {
                            icon: Map,
                            color: "#3EB489",
                            soft: "#DBF3E9",
                            title: "Follow disciplines you love",
                            body: "A live map of every field of knowledge. Follow the fields that grip you — build with people already in them.",
                        },
                        {
                            icon: Users,
                            color: "#6F00FF",
                            soft: "#EEE4FF",
                            title: "Join live global projects",
                            body: "Jump straight into visual brainstorming boards and claim beginner tasks. Real work, real teams, real people.",
                        },
                        {
                            icon: ShieldCheck,
                            color: "#FFC107",
                            soft: "#FFF3CC",
                            title: "Verified by your peers",
                            body: "Every contribution is peer-verified and permanently stamped onto your digital profile and resume.",
                        },
                    ].map((m, i) => {
                        const Icon = m.icon;
                        return (
                            <motion.div
                                key={m.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 0.06 * (i + 1) }}
                                className="crystal rounded-3xl p-6"
                                style={{ background: `linear-gradient(180deg, ${m.soft}, #ffffff)` }}
                                data-testid={`about-mechanic-${i + 1}`}
                            >
                                <div
                                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                                    style={{ background: m.color, boxShadow: `0 8px 24px ${m.color}55` }}
                                >
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="font-display font-black text-xl tracking-tight text-slate-900 mt-4 leading-tight">
                                    {m.title}
                                </h3>
                                <p className="text-slate-700 text-sm mt-2 leading-relaxed">
                                    {m.body}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Transformation callout — center piece */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mt-14 crystal rounded-3xl p-8 sm:p-12 max-w-3xl mx-auto text-center"
                    style={{ background: "linear-gradient(135deg, rgba(111,0,255,0.08), rgba(62,180,137,0.08), rgba(255,193,7,0.08))" }}
                    data-testid="about-transformation"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <ScrollText className="w-4 h-4 text-humanities" />
                        <p className="text-xs uppercase tracking-widest font-bold text-humanities">
                            Your resume, transformed
                        </p>
                    </div>
                    <h3 className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter text-slate-900 leading-[1.05]">
                        From a boring sheet of paper
                        <br />
                        into a <span className="text-humanities">living, un-fakeable record</span>
                        <br className="hidden sm:block" />
                        of your true curiosity and real-world output.
                    </h3>
                </motion.div>

                {/* Outcome — bold closing statement */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mt-12 text-center max-w-3xl mx-auto"
                    data-testid="about-outcome"
                >
                    <div className="inline-flex items-center gap-2 mb-4 chip" style={{ background: "rgba(255,255,255,0.55)", color: "#0A2540" }}>
                        <TrendingUp className="w-3 h-3" /> The outcome
                    </div>
                    <p
                        className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-white leading-[1.15]"
                        style={{ textShadow: "0 3px 30px rgba(10,37,64,0.4)" }}
                    >
                        Be a part of cool projects. Build awesome things. Learn new skills.
                        <br className="hidden sm:block" />
                        Grow your career <span className="text-arts">without barriers or setbacks.</span>
                    </p>
                </motion.div>
            </div>
        </section>
    );
}
